<template>
  <div>
    <div v-if="!options || options.length === 0" class="has-text-centered p-4">
      No Blanket VOB Inquiries found
    </div>
    <div v-else>
      <b-field>
        <b-button
          icon-left="delete"
          type="is-danger"
          v-if="checkedRows.length && isManager && !adminMode"
          @click="handleBulkRemove"
        >
          Remove selected Blanket VOB Searches
        </b-button>
      </b-field>

      <b-table
        ref="table"
        :data="groupedData"
        :loading="loading"
        hoverable
        :row-class="getRowClass"
        :custom-row-key="'row_id'"
        :checkable="isManager && !adminMode"
        :checked-rows.sync="checkedRows"
      >
        <b-table-column field="full_name" label="Patient" v-slot="props">
          <div class="is-flex is-align-items-center">
            <template v-if="props.row.plan_status !== 'Inactive'">
              <router-link
                :to="{
                  name: adminMode ? 'AdminInsuranceDiscoveryDetails' : 'InsuranceDiscoveryDetails',
                  params: {
                    id: props.row.id,
                    payerResponseId: getFirstActivePayerId(props.row.payers),
                  },
                }"
              >
                {{ props.row.full_name || '-' }}
              </router-link>
            </template>
            <template v-else>
              {{ props.row.full_name || '-' }}
            </template>
          </div>
        </b-table-column>

        <b-table-column field="member_id" label="Member ID" v-slot="props">
          {{ props.row.member_id || '-' }}
        </b-table-column>

        <b-table-column field="state" label="State" v-slot="props">
          {{ props.row.state || '-' }}
        </b-table-column>

        <b-table-column field="payer_name" label="Payer" v-slot="props">
          <template v-if="props.row.has_payers">
            <b-dropdown class="payer-dropdown">
              <template #trigger>
                <b-tag class="payer-tag" type="is-info" size="is-small">
                  {{ props.row.payer_count }} Payer{{ props.row.payer_count > 1 ? 's' : '' }}
                </b-tag>
              </template>
              <b-dropdown-item
                v-for="payer in props.row.payers"
                :key="payer.id"
                @click="viewPayerDetails(payer)"
              >
                <span>{{ payer.payer_name }}</span>
                <b-tag :type="getStatusType(payer.status, true)" size="is-small" class="ml-2">
                  {{ payer.status }}
                </b-tag>
              </b-dropdown-item>
            </b-dropdown>
          </template>
          <template v-else>
            {{ props.row.payer_name || '-' }}
          </template>
        </b-table-column>

        <b-table-column field="user" label="User" v-slot="props">
          {{ props.row.user || '-' }}
        </b-table-column>

        <b-table-column field="organization" label="Organization" v-slot="props" v-if="adminMode">
          {{ props.row.organization || '-' }}
        </b-table-column>

        <b-table-column field="created_at" label="Created At" v-slot="props">
          {{ formatDate(props.row.created_at) }}
        </b-table-column>

        <b-table-column field="plan_status" label="Plan Status" v-slot="props">
          <template v-if="props.row.has_payers">
            <div class="tags">
              <template v-for="status in props.row.unique_statuses">
                <b-tag
                  :key="status"
                  :type="getStatusType(status, true)"
                  size="is-small"
                  class="mr-1"
                >
                  {{
                    getStatusCount(props.row.payers, status) === 1
                      ? status
                      : getStatusCount(props.row.payers, status) + ' x ' + status
                  }}
                </b-tag>
              </template>
            </div>
          </template>
          <template v-else>
            <b-tag :type="getStatusType(props.row.plan_status, true)" size="is-small">
              {{ props.row.plan_status || '' }}
            </b-tag>
          </template>
        </b-table-column>

        <b-table-column field="status" label="Status" v-slot="props">
          <b-tag :type="getStatusType(props.row.status)">
            {{ props.row.status || '-' }}
          </b-tag>
        </b-table-column>

        <b-table-column field="actions" label="Actions" v-slot="props" v-if="!adminMode">
          <template v-if="props.row.plan_status !== 'Inactive'">
            <b-icon
              icon="delete"
              class="delete-icon"
              @click.native="handleDelete(props.row)"
            ></b-icon>
          </template>
          <template v-else>
            <b-icon icon="delete" class="has-text-grey-light"></b-icon>
          </template>
        </b-table-column>
      </b-table>

      <div class="pagination-container">
        <b-pagination
          v-model="currentPageLocal"
          :total="totalItems"
          :per-page="pageSize"
          @change="emitPageChange"
        ></b-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import BTable from 'buefy/src/components/table/Table';
import BTableColumn from 'buefy/src/components/table/TableColumn';
import BTag from 'buefy/src/components/tag/Tag';
import BIcon from 'buefy/src/components/icon/Icon';
import BDropdown from 'buefy/src/components/dropdown/Dropdown';
import BDropdownItem from 'buefy/src/components/dropdown/DropdownItem';
import BPagination from 'buefy/src/components/pagination/Pagination';
import { mapGetters } from 'vuex';

export default {
  name: 'InsuranceDiscoveryTable',
  components: {
    BTable,
    BTableColumn,
    BTag,
    BIcon,
    BDropdown,
    BDropdownItem,
    BPagination,
  },
  props: {
    options: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    currentPage: {
      type: Number,
      required: true,
    },
    pageSize: {
      type: Number,
      required: true,
    },
    totalItems: {
      type: Number,
      required: true,
    },
    adminMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentPageLocal: this.currentPage,
      checkedRows: [],
    };
  },
  computed: {
    ...mapGetters(['isManager']),
    groupedData() {
      if (!this.options) return [];
      const result = [];
      this.options.forEach((discovery) => {
        const hasPayers =
          discovery.insurance_discovery_payers && discovery.insurance_discovery_payers.length > 0;
        if (hasPayers) {
          // Group by discovery
          const uniqueStatuses = [
            ...new Set(discovery.insurance_discovery_payers.map((p) => p.status)),
          ];
          result.push({
            ...discovery,
            has_payers: true,
            payer_count: discovery.insurance_discovery_payers.length,
            unique_statuses: uniqueStatuses,
            payers: discovery.insurance_discovery_payers,
            row_id: discovery.id,
          });
        } else {
          // Single row for discovery without payers
          result.push({
            ...discovery,
            has_payers: false,
            payer_name: '-',
            plan_status: 'No payer detected',
            row_id: `${discovery.id}-no-payer`,
          });
        }
      });
      return result;
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString();
    },
    getStatusType(status, isPlanStatus = false) {
      if (isPlanStatus) {
        const planTypes = {
          active: 'is-light-success',
          inactive: 'is-light-danger',
        };
        return planTypes[status?.toLowerCase()] || 'is-grey';
      }
      const types = {
        complete: 'is-success',
        error: 'is-danger',
        pending: 'is-warning',
        active: 'is-grey',
        inactive: 'is-grey',
      };
      return types[status?.toLowerCase()] || 'is-grey';
    },
    viewDetails(row) {
      this.$router.push({
        name: 'InsuranceDiscoveryDetails',
        params: {
          id: row.id,
          payerResponseId: row.payer_details?.id,
        },
      });
    },
    getRowClass(row) {
      // Find the index of this group in the array
      const groupIndex = this.options.findIndex((item) => item.id === row.id);
      // Alternate background colors for different groups
      return groupIndex % 2 === 0 ? 'is-even-group' : 'is-odd-group';
    },
    getStatusCount(payers, status) {
      return payers.filter((p) => p.status === status).length;
    },
    getFirstActivePayerId(payers) {
      if (!payers || payers.length === 0) return null;
      const activePayer = payers.find((p) => p.status.toLowerCase() === 'active');
      return activePayer ? activePayer.id : payers[0].id;
    },
    viewPayerDetails(payer) {
      this.$router.push({
        name: this.adminMode ? 'AdminInsuranceDiscoveryDetails' : 'InsuranceDiscoveryDetails',
        params: { id: payer.insurance_discovery_id, payerResponseId: payer.id },
      });
    },
    handleDelete(row) {
      this.$buefy.dialog.confirm({
        title: 'Delete Blanket VOB Search',
        message:
          'Are you sure you want to delete this Blanket VOB Search? This action cannot be undone.',
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          this.$emit('delete', row.id);
        },
      });
    },
    emitPageChange(newPage) {
      this.$emit('page-change', newPage);
    },
    handleBulkRemove() {
      let message = `Are you sure you want to remove ${this.checkedRows.length} Blanket VOB Searches?`;
      message += `<br/><br/>NOTE: This will not delete the related data, but these searches will no longer be visible to you or your organization's users.`;

      this.$buefy.dialog.confirm({
        title: 'Remove Blanket VOB Searches',
        message: message,
        confirmText: `Remove ${this.checkedRows.length} searches`,
        type: 'is-danger',
        onConfirm: () => {
          let ids = this.checkedRows.map((r) => r.id);
          this.$emit('bulkremove', ids);
          this.checkedRows = [];
        },
      });
    },
  },
};
</script>

<style scoped>
.details-button {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border: none;
  background: none;
  color: #3273dc;
  font-size: 0.9rem;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.details-button:hover {
  background-color: #f5f5f5;
}

.details-button .icon {
  font-size: 0.9rem;
}

/* Status tag colors */
.tag.is-success {
  background-color: #48c774;
  color: white;
}

.tag.is-danger {
  background-color: #f14668;
  color: white;
}

.tag.is-warning {
  background-color: #ffdd57;
  color: rgba(0, 0, 0, 0.7);
}

.tag.is-grey {
  background-color: #f5f5f5;
  color: #7a7a7a;
}

.tag.is-light-success {
  background-color: #effaf3;
  color: #48c774;
}

.tag.is-light-danger {
  background-color: #feecf0;
  color: #f14668;
}

/* Dialog styles */
:deep(.payer-details-dialog) {
  padding: 0.5rem 0;
}

:deep(.payer-details-dialog .detail-row) {
  margin-bottom: 0.5rem;
}

:deep(.payer-details-dialog .detail-row:last-child) {
  margin-bottom: 0;
}

/* Group styling */
:deep(.b-table .table tr.is-even-group) {
  background-color: #ffffff !important;
}

:deep(.b-table .table tr.is-odd-group) {
  background-color: #f5f5f5 !important;
}

:deep(.b-table .table tr.is-even-group:hover) {
  background-color: #fafafa !important;
}

:deep(.b-table .table tr.is-odd-group:hover) {
  background-color: #eeeeee !important;
}

/* Add a subtle border to separate groups */
:deep(.b-table .table-wrapper) {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

:deep(.b-table .table) {
  border-collapse: separate;
  border-spacing: 0;
}

/* Override Buefy's hover styles */
:deep(.b-table .table tr:hover) {
  background-color: inherit !important;
}

.payer-details {
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: 4px;
  margin: 0.5rem 0;
}

.payer-row {
  padding: 0.75rem;
  border-bottom: 1px solid #eee;
}

.payer-row:last-child {
  border-bottom: none;
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.expand-icon {
  display: none;
}

.payer-details {
  display: none;
}

.b-dropdown-menu {
  padding: 0.5rem;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-left: 3px solid #3273dc;
}

.b-dropdown-item {
  padding: 0.5rem;
  border-radius: 4px;
  transition: background-color 0.2s;
  border-bottom: 1px solid #eee;
}

.b-dropdown-item:last-child {
  border-bottom: none;
}

.b-dropdown-item:hover {
  background-color: #f5f5f5;
}

.b-dropdown-item span {
  font-weight: 500;
  color: #000;
}

.b-dropdown-item .tag {
  margin-left: 0.5rem;
}

.b-tag.is-info {
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  border-bottom: 3px solid #3273dc !important;
}

:deep(.dropdown-content) {
  border-top: 3px solid #3273dc !important;
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
  border-radius: 0 0 4px 4px !important;
  margin-top: 0 !important;
  padding: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.payer-tag {
  background-color: #3298dc !important;
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  border-bottom: 3px solid #3298dc !important;
  margin-bottom: 0 !important;
}

:deep(.payer-dropdown .dropdown-content) {
  border-top: 3px solid #3298dc !important;
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
  border-radius: 0 0 4px 4px !important;
  margin-top: 0 !important;
  min-width: 100% !important;
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.1),
    0 -2px 0 0 #3298dc;
  padding: 0.5rem;
}

.delete-icon {
  cursor: pointer;
  color: #f14668;
}

.delete-icon:hover {
  color: #d32f53;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}
</style>
